// Module `events` (API-v3 8): staff event pages, guest and room views.
// Owned by WP events.
import { z } from "zod";
import {
  EVENT_FORMATS,
  EVENT_KINDS,
  EVENT_QUESTION_KINDS,
  JOINING_MODES,
  GENDER_ANSWERS,
  PROGRAMME_BLOCK_TYPES,
  REGISTRATION_ROLES,
  SESSION_INVITATION_STATUSES,
  SESSION_PERSON_ROLES,
  STAKEHOLDER_TYPES,
  type EventFormat,
  type EventKind,
  type GenderAnswer,
  type EventQuestionKind,
  type JoiningMode,
  type IndicatorKind,
  type ProgrammeBlockType,
  type RegistrationRole,
  type SessionInvitationStatus,
  type SessionPersonRole,
  type StakeholderType,
} from "../enums";
import { COMMITMENT_STATUSES, COST_TYPES, type CostCounts, type CostType } from "../costs";
import type { GenderBalanceView } from "../gender-balance";
import type { PresentationStatus, SpeakerDetailsStatus } from "../presentation-status";
import { LIMITS } from "../limits";
import { hasLinkPlaceholder, SPEAKER_EMAIL_LINK_MISSING, unknownPlaceholders, type SpeakerEmailValues } from "../speaker-email";
import { uuid } from "../schemas/common";
import type { ActivityApproval, ActivityHeader } from "./activities";
import { RSVP_LANGUAGES, type RsvpLanguage } from "./events-i18n";
export * from "./events-i18n";
import {
  country,
  email,
  isoDate,
  text,
  textOrEmpty,
  textOrNull,
  timeOfDay,
  centsV3,
  type FileRef,
  type Id,
  type Link,
  type StatusChip,
} from "./common";

export type { EventQuestionKind as QuestionKindV3, RegistrationRole };
export const registrationRole = z.enum(REGISTRATION_ROLES);

// ---------- 8.1 staff event pages ----------

/**
 * The invitation status as shown, the same list everywhere (Marius's event
 * space, section 7): key and label are Not invited · Invited · Confirmed ·
 * Declined; `note` names an in-between state ("Waiting list", "Maybe",
 * "On hold", "Cancelled", "Opened"). Built by guestInvitationStatus /
 * invitationStatusView in @convenor/shared.
 */
export type InvitationChip = StatusChip & { key: SessionInvitationStatus; note: string | null };

/** The event tabs, in Marius's order. */
export const EVENT_TAB_KEYS = ["overview", "programme", "logistics", "invitations", "people", "check-in", "follow-up"] as const;
export type EventTabKey = (typeof EVENT_TAB_KEYS)[number];

/**
 * The event settings that drive other screens (Marius's event space, section
 * 8): the format and online link (the RSVP question "How will you join?", the
 * Joining column, online check-in, the link in emails) and the gender balance.
 */
export type EventSettings = {
  format: EventFormat;
  formatLabel: string;
  /** The link, meeting ID and passcode: only for staff who may edit the event (null for everyone else). */
  onlineUrl: string | null;
  onlineMeetingId: string | null;
  onlinePasscode: string | null;
  /** Whether a link is set (for everyone, without the link itself). */
  onlineLinkSet: boolean;
  /** Send the link only to people who choose to join online (hybrid events). */
  onlineLinkOnlyOnline: boolean;
  genderBalance: { on: boolean; maxPct: number };
  /** Round 4: who the balance counts; "speakers" also flags sessions with only one gender. */
  genderBalanceApplyTo?: GenderBalanceApplyTo;
};

export const GENDER_BALANCE_APPLY_TO = ["everyone", "speakers"] as const;
export type GenderBalanceApplyTo = (typeof GENDER_BALANCE_APPLY_TO)[number];

export type EventOverview = {
  header: ActivityHeader;
  /** Additive: the settings that drive other screens. */
  settings?: EventSettings;
  /** Additive: "RSVP closes in 5 days · 38 of 50 places", or null without a deadline or capacity. */
  countdown?: string | null;
  /** Additive: the gender balance of the confirmed people; null when the event does not track it. */
  balance?: GenderBalanceView | null;
  /** Additive: the gender balance of the speakers and moderators on the programme; null when not tracked. */
  programmeBalance?: GenderBalanceView | null;
  sendInvitations: { enabled: boolean; reason: string | null };
  /** E1: the blocks in order; blocks of one day with the same parallelGroup run at the same time. */
  programme: ProgrammeBlockView[];
  guestPreview: {
    total: number;
    administrations: number;
    rows: { name: string; country: string | null; organisation: string | null; role: string; invitation: InvitationChip; /** The person's stakeholder record, when there is one. */ path?: string | null }[];
  };
  details: {
    result: string | null;
    budget: string | null;
    plannedParticipants: number | null;
    /** At least this share of women, from the gender balance setting (null when balance is off). */
    targetWomenPct: number | null;
    experts: { name: string; path: string }[];
    evidence: { label: string; state: StatusChip; path: string | null }[];
  };
  collectedAfter: { label: string; state: StatusChip }[];
  tabs: { key: EventTabKey; label: string; count: number | null }[];
  /** v3 (WP events, additive): the values the Edit event form starts from. */
  edit?: {
    title: string;
    code: string;
    startDate: string;
    endDate: string;
    venue: string;
    venueAddress: string;
    mapUrl: string | null;
    plannedParticipants: number | null;
    description: string;
    format?: EventFormat;
    /** A1/A16 (Analytics): workshop, training, conference… (PATCH kind). */
    kind?: EventKind;
    onlineUrl?: string | null;
    onlineMeetingId?: string | null;
    onlinePasscode?: string | null;
    onlineLinkOnlyOnline?: boolean;
    genderBalanceOn?: boolean;
    genderBalanceMaxPct?: number;
    genderBalanceApplyTo?: GenderBalanceApplyTo;
    /** E6: guests see the confirmed speakers and moderators on the programme (PATCH programmeShowPeople). */
    programmeShowPeople?: boolean;
    /** E2 (retest 30 Oct): the programme's subtitle, stored as the RSVP invitation text (event.rsvp_intro); null = the standard text. */
    subtitle?: string | null;
    /** E2: the practical details (event.practical_info), also on the RSVP form settings. */
    practicalInfo?: string;
    /** D6 "Needs the funder's approval", for staff who may edit (null otherwise, or for an event without an activity). */
    approval?: ActivityApproval | null;
    /** AC-N5 (retest 30 Oct): the approved ToR's dates, so changed dates are questioned before saving; null without one. */
    approvedTor?: { startDate: string | null; endDate: string | null; path: string } | null;
  };
  /** v3 (additive): invitations held while the ToR is not approved. */
  lock?: { locked: boolean; reason: string | null };
  /** Additive: "Closed on 12 October 2026 by Ana Petrova" when the event is closed, else null. */
  closedLine?: string | null;
  /** v3 (additive): the programme as stored, for the programme editor. */
  sessions?: ProgrammeSessionStored[];
  /** Fix round 1: when the programme was last saved (sent back with a save). */
  programmeSavedAt?: string | null;
  /** E1 (additive): the project experts who can be put on the programme. */
  programmeExperts?: { id: Id; name: string; sub: string }[];
  /** E9 (retest 30 Oct, additive): team colleagues (app users of the project team) who can be put on the programme. */
  programmeColleagues?: { id: Id; name: string; sub: string }[];
  /** E3 (retest 30 Oct, additive): "About the event", the text blocks in their order. */
  programmeTexts?: ProgrammeText[];
  /**
   * Additive: "The ToR was approved on 3 October 2026. You can now send
   * invitations and log costs." Shown to people who may edit the event, for
   * 30 days after the approval, until they put it away (per person). Null otherwise.
   */
  unlocked?: { approvedOn: string; message: string } | null;
  /**
   * E2 (additive): confirmed project experts on the programme whose day is
   * not on their timesheet yet, asked "Add 1 day to ...'s timesheet?". Only
   * for staff who may edit timesheets (never partners or viewers); empty otherwise.
   */
  timesheetSuggestions?: TimesheetSuggestion[];
  /** Round 4 (additive): the progress steps across the top of the Overview. */
  progress?: EventProgress;
  /** Round 4 (additive): the four figure cards (the budget card only for people who see the budget). */
  figures?: EventFigure[];
  /** Round 4 (additive): "Needs your attention" for this event, each with its action. Empty for people who may not edit. */
  attention?: EventAttentionItem[];
  /** Round 4 (additive): "Evidence for the report", a status per item. */
  reportEvidence?: { label: string; state: StatusChip; path: string | null }[];
  /** Round 5 (EV5): how much of the one evidence table (on Follow-up) is ready; Overview links there instead of repeating it. */
  evidenceReady?: { ready: number; total: number; path: string };
  /** Round 4 (additive): the latest lines of the activity log about this event; null for partners. */
  recentActivity?: { id: Id; summary: string; at: string }[] | null;
  /** Round 4 (additive): the Details panel. */
  facts?: EventFacts;
  /** Round 4 (additive): sessions with speakers and moderators of only one gender, when the balance applies to them. */
  oneGenderSessions?: { id: Id; title: string; gender: string }[];
};

/** skipped (additive, EV3): not done on a closed or cancelled event, so no longer needed. */
export type EventStepState = "done" | "current" | "todo" | "skipped";
export type EventProgress = {
  /** Days until the first day; null once it has started. */
  daysToGo: number | null;
  /** late: not done and past when it should be (the ToR, RSVP and invitations a week before the RSVP deadline; shown in amber). */
  steps: { key: "tor" | "rsvp" | "invitations" | "speakers" | "check_in" | "follow_up"; label: string; sub: string; state: EventStepState; late?: boolean }[];
};
export type EventFigure = {
  key: "confirmed" | "speakers" | "gender" | "budget";
  label: string;
  value: string;
  /** 0 to 100 for the thin bar. */
  bar: number | null;
  /** Additive: 0 to 100, a target tick on the bar (gender balance: at least 40% women). */
  target?: number;
  sub: string;
  /** "Logistics" on the budget card. */
  link: { label: string; path: string } | null;
  /** Budget card: "€10,343 over the approved budget" when spent is over the approved ToR. */
  warning?: string;
};
export type EventAttentionItem = {
  id: string;
  /** warning: someone has not answered, or spent is over the budget; travel: travel support; info: the programme. */
  kind: "warning" | "travel" | "info";
  title: string;
  detail: string;
  action: { label: string; path: string };
};
export type EventFacts = {
  format: string;
  venue: string | null;
  /** "Teams link · ID 384 221 907"; the link itself only for staff who may edit. */
  online: { url: string | null; label: string } | null;
  result: string | null;
  tor: { label: string; path: string } | null;
  experts: { name: string; path: string }[];
  team: string[];
};

/** E2: one "Add 1 day to Jean Dupont's timesheet for 12 October 2026?" question. */
export type TimesheetSuggestion = {
  sessionId: Id;
  engagementId: Id;
  expertName: string;
  sessionTitle: string;
  /** The day of the block (YYYY-MM-DD). */
  date: string;
  question: string;
  /** Set when the day cannot be added: the timesheet of that month is already signed. */
  blockedReason: string | null;
  /** AC-F8: a signed month: where the timesheet is corrected ("Ask Ana Petrova to correct the September timesheet"). */
  correct?: { label: string; path: string } | null;
};

/** E2: add the day (a full day or half a day) to the expert's timesheet as a draft line. */
export const timesheetDayBody = z.object({ sessionId: uuid, engagementId: uuid, halves: z.union([z.literal(1), z.literal(2)]) });
/** E2: "Not needed" for one expert on one block. */
export const timesheetDayDismissBody = z.object({ sessionId: uuid, engagementId: uuid });
/** One person on a programme block, as shown. */
export type ProgrammePersonView = {
  id: Id;
  role: SessionPersonRole;
  roleLabel: string;
  /** With the title from the stakeholder record ("Dr Ana Petrova"). */
  name: string;
  sub: string;
  /** The stakeholder record or the expert page; null for a name only. */
  path: string | null;
  invitationStatus: SessionInvitationStatus;
  invitation: InvitationChip;
  /** The guest's speaker details page (guest rows only). */
  detailsPath: string | null;
  /** Additive: Not requested / Requested / Received (guest rows only). */
  speakerDetails?: SpeakerDetailsChip | null;
  /** Fix round 1: the guest row, so the editor pairs the details with the right person. */
  registrationId?: Id | null;
  /** E18 (retest 30 Oct): the job title (the stakeholder record's position, else the guest row's); null when not known. */
  position?: string | null;
  /** E18: a typed name, not linked to a stakeholder, expert or colleague yet; only present when true. */
  typed?: boolean;
};
export type ProgrammeBlockView = {
  id: Id;
  day: number;
  startTime: string;
  /** EV9: when the block ends; null = not given. */
  endTime?: string | null;
  label: string;
  title: string;
  blockType: ProgrammeBlockType;
  blockLabel: string;
  parallelGroup: number | null;
  /** E4 (retest 30 Oct): one or two short paragraphs about the block; "" = none. */
  description?: string;
  people: ProgrammePersonView[];
};
/** E3 (retest 30 Oct): one "About the event" text block (Introduction, Objectives...). An empty body is not shown to guests. */
export type ProgrammeText = { id: Id; heading: string; body: string };
/**
 * One person on a block as stored. EV2 (round 5): every person is a guest row
 * of the event (registrationId); engagementId is set when that guest is a
 * project expert; label is always null (typed names became guest rows).
 */
export type ProgrammePersonStored = {
  registrationId: Id | null;
  engagementId: Id | null;
  label: string | null;
  /** EV2: the guest row has no email yet (a typed name): they cannot be emailed until one is added on People. */
  noEmail?: boolean;
  /** E9 (retest 30 Oct): the guest is a team colleague (an app user); only present when set. */
  userId?: Id | null;
  /** E18: "job title · organisation", or "" when neither is known; only present when known. */
  sub?: string;
  /** E18: a typed name, not linked to a stakeholder, expert or colleague yet; only present when true. */
  typed?: boolean;
  /** Shown in the editor. */
  name: string;
  role: SessionPersonRole;
  invitationStatus: SessionInvitationStatus;
};
export type ProgrammeSessionStored = {
  id: Id;
  day: number;
  startTime: string;
  /** EV9: when the block ends; null = not given. */
  endTime?: string | null;
  title: string;
  blockType: ProgrammeBlockType;
  parallelGroup: number | null;
  /** E4 (retest 30 Oct): the block's description; "" = none. */
  description?: string;
  people: ProgrammePersonStored[];
};
export const editEventBody = z
  .object({
    title: text(LIMITS.eventTitle, "Title").optional(),
    code: z.string().trim().min(1).max(LIMITS.activityCodeV3).optional(),
    startDate: isoDate.optional(),
    endDate: isoDate.optional(),
    venue: textOrEmpty(LIMITS.location).optional(),
    venueAddress: textOrEmpty(500).optional(),
    mapUrl: z.string().trim().url().max(LIMITS.url).nullable().optional(),
    plannedParticipants: z.number().int().min(0).max(LIMITS.capacityMax).nullable().optional(),
    format: z.enum(EVENT_FORMATS).optional(),
    /** A1/A16 (Analytics): the kind of event, for the Analytics filter and "What an event costs". */
    kind: z.enum(EVENT_KINDS).optional(),
    onlineUrl: z.string().trim().url("Enter the full link, starting with https://").max(LIMITS.url).nullable().optional(),
    onlineMeetingId: z.string().trim().max(100).nullable().optional(),
    onlinePasscode: z.string().trim().max(100).nullable().optional(),
    onlineLinkOnlyOnline: z.boolean().optional(),
    genderBalanceOn: z.boolean().optional(),
    genderBalanceMaxPct: z.number().int().min(50, "Enter a percentage from 50 to 100.").max(100, "Enter a percentage from 50 to 100.").optional(),
    genderBalanceApplyTo: z.enum(GENDER_BALANCE_APPLY_TO).optional(),
    /** E6 (retest 30 Oct): guests see the confirmed speakers and moderators on the programme. */
    programmeShowPeople: z.boolean().optional(),
  })
  .strict();
export type EditEventBody = z.infer<typeof editEventBody>;
/**
 * A speaker or moderator on a block (E1). Exactly one of: registrationId (a
 * guest row of this event), contactId (a stakeholder: their guest row is made
 * or reused), engagementId (a project expert: their guest row, no stakeholder
 * record), userId (a team colleague, E9: their guest row, no stakeholder
 * record) or label (a typed name, "TBC": a guest row with that name, EV2).
 * New people start as Not invited (EV9).
 */
export const programmePersonBody = z
  .object({
    registrationId: uuid.nullish(),
    contactId: uuid.nullish(),
    engagementId: uuid.nullish(),
    userId: uuid.nullish(),
    label: z.string().trim().max(200).nullish(),
    role: z.enum(SESSION_PERSON_ROLES),
    invitationStatus: z.enum(SESSION_INVITATION_STATUSES).default("not_invited"),
    /**
     * EV-F4 (round 3 feedback, additive): the status the editor was shown. When
     * it equals invitationStatus the save does not change the person's status,
     * so a status set on People since the page was opened stays.
     */
    statusSeen: z.enum(SESSION_INVITATION_STATUSES).optional(),
  })
  .refine((p) => [p.registrationId, p.contactId, p.engagementId, p.userId, p.label || null].filter((v) => v != null).length === 1, "Choose one person, one expert or type a name.");
/** E3 (retest 30 Oct): an "About the event" block as saved; without id it is new. */
export const programmeTextBody = z.object({
  id: uuid.optional(),
  heading: z.string().trim().max(200, "The heading must be 200 characters or fewer."),
  body: z.string().trim().max(5_000, "The text must be 5,000 characters or fewer."),
});
export const programmeBody = z.object({
  sessions: z
    .array(
      z.object({
        id: uuid.optional(),
        day: z.number().int().min(1).max(31),
        startTime: timeOfDay,
        /** EV9: optional end time, after the start. */
        endTime: timeOfDay.nullable().optional(),
        title: text(300, "Session title"),
        blockType: z.enum(PROGRAMME_BLOCK_TYPES).default("session"),
        parallelGroup: z.number().int().min(1).max(99).nullable().default(null),
        /** E4 (retest 30 Oct): left out = unchanged. */
        description: z.string().trim().max(2_000, "The description must be 2,000 characters or fewer.").optional(),
        people: z.array(programmePersonBody).max(30).default([]),
      })
      .refine((s) => !s.endTime || s.endTime > s.startTime, { message: "The end time must be after the start time.", path: ["endTime"] }),
    )
    .max(100),
  /** Fix round 1: the programmeSavedAt the editor started from (null: never saved); a newer save meanwhile gives 409. Left out: no check. */
  updatedAt: z.string().max(40).nullable().optional(),
  /** E3 (retest 30 Oct): "About the event" in its order; left out = unchanged. */
  about: z.array(programmeTextBody).max(30).optional(),
  /**
   * E2 (retest 30 Oct): the event card. subtitle is the RSVP invitation text
   * (event.rsvp_intro; empty = the standard text), practicalInfo the practical
   * details (event.practical_info). Left out = unchanged.
   */
  details: z
    .object({
      subtitle: z.string().trim().max(LIMITS.rsvpIntro, `The subtitle must be ${LIMITS.rsvpIntro} characters or fewer.`).nullable(),
      practicalInfo: z.string().trim().max(5_000, "The practical details must be 5,000 characters or fewer."),
    })
    .optional(),
});
export type ProgrammeBody = z.input<typeof programmeBody>;
export type ProgrammePersonBody = z.input<typeof programmePersonBody>;

export const questionForm = z.enum(["rsvp", "feedback"]);
export const questionBody = z.object({
  label: text(LIMITS.questionLabel, "Question"),
  kind: z.enum(EVENT_QUESTION_KINDS),
  options: z.array(text(LIMITS.questionOption, "Option")).max(LIMITS.questionOptionsMax),
  /** E11 (feedback form, Grid): the rows; `options` are the shared columns. */
  rows: z.array(text(LIMITS.questionOption, "Row")).max(LIMITS.questionOptionsMax).optional(),
  required: z.boolean(),
  useForReporting: z.boolean(),
  /** R3 (feedback form only): the indicator this question counts towards, a share from answers of the project. */
  indicatorId: uuid.nullable().optional(),
  /** R3: the answers that count as "yes" for the share (scale "4", "5"; yes/no "yes"; a choice's options). */
  countsAs: z.array(text(LIMITS.questionOption, "Answer")).max(LIMITS.questionOptionsMax).optional(),
  /**
   * Round 4: the question in the form's other languages (RSVP form). The
   * options are shown in that language, in the same order; answers keep the
   * English option. Empty text = the English text is shown.
   */
  translations: z
    .record(
      z.enum(RSVP_LANGUAGES),
      z.object({
        label: z.string().trim().max(LIMITS.questionLabel, "The question is too long."),
        options: z.array(z.string().trim().max(LIMITS.questionOption, "An option is too long.")).max(LIMITS.questionOptionsMax),
      }),
    )
    .optional(),
});
export type QuestionBody = z.infer<typeof questionBody>;
export type QuestionV3 = QuestionBody & {
  id: Id;
  /** Round 4: on a guest page in another language, the options as shown (same order as `options`, which stay the answer values). */
  optionLabels?: string[];
  position: number;
  standardKey: "usefulness" | "attend_again" | "attendance" | null;
  fromRecord: boolean;
};
export const reorderBody = z.object({ ids: z.array(uuid).max(LIMITS.questionsMaxV3) });

export const guestReceives = z.object({
  invitation: z.boolean(),
  confirmationIcs: z.boolean(),
  practicalInfo: z.boolean(),
  reminder5Days: z.boolean(),
  updateOnChange: z.boolean(),
});
export type GuestReceives = z.infer<typeof guestReceives>;
export const rsvpFormBody = z.object({
  deadline: isoDate.nullable(),
  capacity: z.number().int().min(1).max(LIMITS.capacityMax).nullable(),
  waitingList: z.boolean(),
  /** Round 4: the language of the guest pages (labels translated, questions per language). */
  language: z.enum(RSVP_LANGUAGES),
  reminderDays: z.number().int().min(1).max(60).nullable(),
  bannerFileId: uuid.nullable(),
  partnerLogoFileIds: z.array(uuid).max(20),
  showEuStatement: z.boolean(),
  guestReceives,
  practicalInfo: textOrEmpty(5_000),
  /** Round 5: the invitation text at the top of the form ({first name}, {salutation}, {event title}); null = the default of the form's language. Left out = no change. */
  intro: z.string().trim().max(LIMITS.rsvpIntro, `The invitation text must be ${LIMITS.rsvpIntro} characters or fewer.`).nullable().optional(),
});
export type RsvpFormBody = z.infer<typeof rsvpFormBody>;

/**
 * Round 5: the standard questions of the RSVP form that staff can move.
 * "Will you attend?" (and "How will you join?") stay first, the consent to
 * store the details stays last.
 */
export const RSVP_STANDARD_QUESTIONS = ["org", "gender", "travel", "diet", "photo"] as const;
export type RsvpStandardQuestion = (typeof RSVP_STANDARD_QUESTIONS)[number];
export const isRsvpStandardQuestion = (k: string): k is RsvpStandardQuestion => (RSVP_STANDARD_QUESTIONS as readonly string[]).includes(k);
/**
 * R2-036 (Marius): the standard questions staff took off the RSVP form, from
 * what is stored (unknown keys dropped). "Will you attend?", the name, the
 * email and the consent to store the details are never removed.
 */
export const rsvpRemoved = (stored: readonly string[] | null | undefined): RsvpStandardQuestion[] => RSVP_STANDARD_QUESTIONS.filter((k) => (stored ?? []).includes(k));

/**
 * The feedback form's standard questions: staff may remove them (R2-036) and
 * put them back. Removed, they are not asked and not counted; answers given
 * before stay stored.
 */
export const FEEDBACK_STANDARD_KEYS = ["usefulness", "attend_again"] as const;
export type FeedbackStandardKey = (typeof FEEDBACK_STANDARD_KEYS)[number];

/**
 * The order of the movable RSVP questions: the stored order (standard keys
 * and question ids) without what no longer exists, then what it misses (a new
 * question goes last). Nothing stored = the standard questions, then staff's.
 */
export function rsvpQuestionOrder(stored: readonly string[] | null | undefined, questionIds: readonly string[]): string[] {
  const known = new Set<string>([...RSVP_STANDARD_QUESTIONS, ...questionIds]);
  const out: string[] = [];
  for (const k of [...(stored ?? []), ...RSVP_STANDARD_QUESTIONS, ...questionIds]) if (known.has(k) && !out.includes(k)) out.push(k);
  return out;
}

/** PUT …/rsvp/order: every movable question once, in the new order. */
export const rsvpOrderBody = z.object({ order: z.array(z.string().max(40)).max(LIMITS.questionsMaxV3 + RSVP_STANDARD_QUESTIONS.length) }).strict();
export type RsvpOrderBody = z.infer<typeof rsvpOrderBody>;

export type RsvpForm = RsvpFormBody & {
  /** Round 5: the text shown when `intro` is null (the form's language). */
  introDefault?: string;
  /** Round 5: the order of the movable questions (rsvpQuestionOrder). */
  order?: string[];
  /** R2-036 (additive): the standard questions taken off the form; `order` still holds them (put back, they return to their place). */
  removed?: RsvpStandardQuestion[];
  questions: QuestionV3[];
  banner: FileRef | null;
  orgLogo: FileRef | null;
  partnerLogos: FileRef[];
  /** v3 (additive): the standard questions' settings (gender optional or required). */
  standard?: { gender: "optional" | "required" };
  /** Round 4 (D8): a draft, or "Your RSVP link · Live" with the shared link. */
  link?: RsvpLinkView;
};
export const previewKind = z.enum(["invitation", "confirmation", "rsvp", "feedback"]);

export type RegistrationRowV3 = {
  id: Id;
  name: string;
  organisation: string | null;
  response: string;
  /** Additive: the invitation status (the same list as People). */
  invitation?: InvitationChip;
  /** Additive: how they join ("In person", "Online"); null when not asked or not coming. */
  joining?: string | null;
  /** Additive: on the waiting list (staff can give them a place). */
  waitlisted?: boolean;
  travelSupport: "yes" | "no" | null;
  respondedAt: string | null;
  /** Round 4 (D8): replied through the shared link and made a new stakeholder record. */
  newInStakeholders?: boolean;
  /** Round 4 (staff only): position, gender, dietary needs, photo consent, how they replied, and an answer per RSVP question. */
  position?: string | null;
  gender?: string | null;
  dietary?: string | null;
  photos?: "Yes" | "No" | null;
  via?: string;
  answers?: Record<string, string>;
  /** Round 4: on the programme (shown in the speakers table instead). */
  onStage?: boolean;
};
/** Round 4: one speaker or moderator on Responses (from the speaker details form). */
export type SpeakerResponseRow = {
  id: Id;
  name: string;
  /** "Head of unit · Ministry of Interior" */
  sub: string;
  roleLabel: string;
  session: string;
  bio: string | null;
  /** "Yes", "No", "Not yet" (no details), "Not asked" (left out of their form). */
  travel: string;
  dietary: string;
  details: StatusChip;
  detailsPath: string;
};
export type RegistrationsView = {
  locked: boolean;
  lockReason: string | null;
  rows: RegistrationRowV3[];
  count: number;
  /** Additive: the gender balance of the confirmed people; null when the event does not track it. */
  balance?: GenderBalanceView | null;
  /** Round 4: the summary cards. */
  summary?: { invited: number; confirmed: number; noReply: number; declined: number };
  /** Round 4 (staff only): the questions of the RSVP form, a column each. */
  questions?: { id: Id; label: string }[];
  /** Round 4 (staff only): the speakers and moderators on the programme. */
  speakers?: SpeakerResponseRow[];
  /** EV-F2 (staff editors only): replies through the shared link waiting for email confirmation. */
  pending?: PendingReplyRow[];
};
export type GuestRow = {
  id: Id;
  name: string;
  position: string | null;
  organisation: string | null;
  country: string | null;
  role: RegistrationRole;
  invitation: InvitationChip;
  contactPath: string | null;
  /**
   * P1/P2: where the details given for this event differ from the stakeholder
   * record (labels, ready to show). Staff with edit rights only; null otherwise
   * or when nothing differs. The guest row is never changed by it.
   */
  recordDiffers: { field: GuestContactField; label: string; atEvent: string; onRecord: string | null }[] | null;
  /** EV-F1 (round 3 feedback): "Review changes" on the stakeholder record (old → new per field); null without differences. */
  reviewPath?: string | null;
  /** Additive: speakers and moderators on the programme only; null for everyone else. */
  speakerDetails?: SpeakerDetailsChip | null;
  /** Additive: "Withdrawn by the person on 2 Oct 2026: photo consent" (their own page); null when nothing was withdrawn. */
  withdrawn?: string | null;
  /** Additive: "Doesn't want to be contacted (since 2 Oct 2026)": Convenor sends them no invitations or reminders; null otherwise. */
  noContact?: string | null;
  /** Round 4: replied through the shared link and made a new stakeholder record. */
  newInStakeholders?: boolean;
  /** Round 4: speakers and moderators: their first session ("Session 2 · 11:00"). */
  session?: string | null;
  /** Round 4: when they were added (sorting "Newest added first"). */
  addedAt?: string;
  /** Round 4: not invited yet, so they can be ticked for "Send invite to N" (speakers and moderators are invited from the programme). */
  canInvite?: boolean;
  /** Fix round 1: the status was set by hand: "Added manually" or "Changed manually · <date>". */
  manual?: { kind: "added" | "changed"; at: string } | null;
};
/** EV-F1: the fields of a guest row compared with the stakeholder record ("Review changes"). */
export const GUEST_CONTACT_FIELDS = ["title", "name", "organisation", "position", "country", "phone", "gender", "dietary", "accessibility", "bio", "photo"] as const;
export type GuestContactField = (typeof GUEST_CONTACT_FIELDS)[number];
export const GUEST_CONTACT_FIELD_LABELS: Record<GuestContactField, string> = {
  title: "Title",
  name: "Name",
  organisation: "Organisation",
  position: "Position",
  country: "Country",
  phone: "Phone",
  gender: "Gender",
  dietary: "Dietary needs",
  accessibility: "Accessibility needs",
  bio: "Short bio",
  photo: "Photo",
};
/**
 * EV-F1: "Review changes" of one answer: the fields to copy to the record
 * (accept); the rest stay as the record says and are not offered again for
 * the same answer. An empty accept = "Keep the record as it is".
 */
export const recordReviewBody = z.object({ accept: z.array(z.enum(GUEST_CONTACT_FIELDS)).max(GUEST_CONTACT_FIELDS.length) }).strict();
export type RecordReviewBody = z.infer<typeof recordReviewBody>;
/**
 * EV-F2: a reply through the shared link that waits until the person
 * confirms it from their inbox (staff editors only; their name and address
 * as typed, nothing stored on the guest list yet).
 */
export type PendingReplyRow = {
  id: Id;
  name: string;
  email: string | null;
  answer: string;
  /** "Known in stakeholders" or "New address". */
  who: string;
  sentAt: string;
  expiresAt: string;
  /** The one reminder went out (or staff sent the email again). */
  remindedAt: string | null;
  canResend: boolean;
};
export type GuestList = {
  /** participants / speakers (round 4, additive): participants, and speakers and moderators. */
  tiles: { onList: number; womenPct: number | null; administrations: number; participants?: number; speakers?: number };
  /** Additive: the gender balance of everyone on the list; null when the event does not track it. */
  balance?: GenderBalanceView | null;
  locked: boolean;
  lockReason: string | null;
  rows: GuestRow[];
};
/**
 * Round 4 (People › Add people): add them as "to invite" (the invitation goes
 * when staff send it), "invited" (invited outside Convenor: no email) or
 * "confirmed" (they confirmed outside Convenor: no email).
 */
export const ADD_GUEST_AS = ["to_invite", "invited", "confirmed"] as const;
export type AddGuestAs = (typeof ADD_GUEST_AS)[number];
export const addGuestsBody = z
  .object({
    contactIds: z.array(uuid).max(LIMITS.inviteContactsMax).optional(),
    listId: uuid.optional(),
    role: registrationRole,
    addAs: z.enum(ADD_GUEST_AS).optional(),
    /** Fix round 1: "People you selected" on Stakeholders: consent is checked for them as for a list. */
    checkConsent: z.boolean().optional(),
  })
  .refine((v) => (v.contactIds?.length ?? 0) > 0 || v.listId, "Choose people or a list.");
/** Round 4: "or add a new stakeholder" on People: a new stakeholder record (or the one with this email) and their row on the list. */
export const newGuestBody = z
  .object({
    name: text(120, "Name"),
    /** Fix round 1: the wireframe's Title (their job title, kept as the position). */
    title: textOrEmpty(200).optional(),
    email,
    organisation: textOrEmpty(LIMITS.organisationName),
    country: country.nullable().optional(),
    gender: z.enum(GENDER_ANSWERS).nullable().optional(),
    /** Round 5 (AN2): the type of their organisation, for Analytics "Who takes part". */
    stakeholderType: z.enum(STAKEHOLDER_TYPES).nullable().optional(),
    role: registrationRole,
    addAs: z.enum(ADD_GUEST_AS),
  })
  .strict();
export type NewGuestBody = z.infer<typeof newGuestBody>;
export type AddGuestsResult = {
  added: number;
  skipped: { name: string; reason: "no_email" | "no_consent" | "no_contact" | "already_invited" }[];
  queued: boolean;
};
export const guestPatch = z.object({
  role: registrationRole.optional(),
  status: z.enum(["invited", "registered", "declined", "cancelled"]).optional(),
});
/**
 * Fix round 1: a speaker's answers to the questions staff added to the speaker
 * form, as label and text ("Yes"/"No" for yes/no), in the form's order. An
 * answer to a question no longer on the form is still listed (it is their data).
 */
export function speakerAnswerItems(
  form: readonly { key: string; label: string; builtIn?: boolean }[] | null | undefined,
  answers: Record<string, string | boolean> | null | undefined,
): { label: string; value: string }[] {
  const given = answers ?? {};
  const text = (v: string | boolean) => (typeof v === "boolean" ? (v ? "Yes" : "No") : v.trim());
  const out: { label: string; value: string }[] = [];
  const seen = new Set<string>();
  for (const q of form ?? []) {
    if (q.builtIn) continue;
    seen.add(q.key);
    const v = given[q.key];
    if (v === undefined || v === null || text(v) === "") continue;
    out.push({ label: q.label, value: text(v) });
  }
  for (const [key, v] of Object.entries(given)) {
    if (seen.has(key) || v === undefined || v === null || text(v) === "") continue;
    out.push({ label: "A question no longer on the form", value: text(v) });
  }
  return out;
}

/** Speaker details of one guest (P5): the stakeholder side (title, bio, photo) and the event side. */
export type SpeakerDetails = {
  /** Fix round 1: their answers to the questions staff added to the speaker form; always given by them. */
  answers?: { label: string; value: string; selfGiven: true }[];
  guest: { id: Id; name: string; role: RegistrationRole };
  /** The stakeholder record the guest is linked to; null when there is none yet. */
  contact: { id: Id; name: string; path: string; title: string | null; bio: string | null; photo: FileRef | null; canEdit: boolean } | null;
  travelSupport: boolean | null;
  dietary: string;
  photoConsent: boolean;
  slides: FileRef | null;
  slidesShareConsent: boolean;
  /** Additive: what the person withdrew themselves (their own page), ready to show; empty when nothing. */
  withdrawn?: string[];
};
export const speakerDetailsBody = z.object({
  title: textOrNull(LIMITS.personTitle, "Title").optional(),
  bio: textOrNull(LIMITS.personBio, "Biography").optional(),
  photoFileId: uuid.nullable().optional(),
  travelSupport: z.boolean().nullable().optional(),
  dietary: z.string().trim().max(500, "Dietary requirements must be 500 characters or fewer.").optional(),
  photoConsent: z.boolean().optional(),
  slidesFileId: uuid.nullable().optional(),
  slidesShareConsent: z.boolean().optional(),
});
export type SpeakerDetailsBody = z.infer<typeof speakerDetailsBody>;

/** Speaker details: Not requested → Requested (date) → Received (date) (Marius 7). */
export type SpeakerDetailsChip = { key: SpeakerDetailsStatus; label: string; tone: "neutral" | "warning" | "success" };
/** One confirmed speaker or moderator on Invitations › Speaker details. */
export type SpeakerRequestRow = {
  registrationId: Id;
  name: string;
  role: "speaker" | "moderator";
  roleLabel: string;
  /** "Session 2, 11:00" */
  session: string;
  hasEmail: boolean;
  details: SpeakerDetailsChip;
  /** "Reminder sent 25 Sep" once the automatic reminder went to them, else null. */
  reminderLabel: string | null;
  /** The staff page with what they sent. */
  detailsPath: string;
  /** Round 4: the questions left out for this person ("Customise"). */
  skip?: string[];
};
/** The request email of the event: its text, the answer-by date and the reminder. */
export type SpeakerRequestEmail = {
  subject: string;
  body: string;
  /** True while the default text is used (nothing saved yet). */
  isDefault: boolean;
  /** The answer-by date for {deadline}; null = the default below. */
  deadline: string | null;
  /** {deadline} when no date is set: a week before the event. */
  defaultDeadline: string;
  /** Days after the request; null = no reminder. */
  reminderDays: number | null;
  /** The values for the preview (the first person on the list, or a sample); deadline is filled in the browser. */
  preview: SpeakerEmailValues;
};
export type SpeakerRequestView = {
  rows: SpeakerRequestRow[];
  /** Round 4: the event's speaker form. */
  form?: SpeakerQuestion[];
  canSend: boolean;
  /** Why nothing can be sent now (closed event), or null. */
  closedMessage: string | null;
  email: SpeakerRequestEmail;
};
/** Save the request email: text, answer-by date and reminder. */
export const speakerEmailBody = z
  .object({
    subject: z
      .string()
      .trim()
      .min(1, "Enter a subject.")
      .max(200, "The subject must be 200 characters or fewer.")
      .refine((s) => !/[\r\n]/.test(s), "Keep the subject on one line."),
    body: z.string().trim().min(1, "Enter the message.").max(5000, "The message must be 5000 characters or fewer."),
    deadline: isoDate.nullable(),
    reminderDays: z.number().int("Enter a whole number of days.").min(1, "Enter a number of days from 1 to 30.").max(30, "Enter a number of days from 1 to 30.").nullable(),
  })
  .superRefine((v, ctx) => {
    if (!hasLinkPlaceholder(v.body)) ctx.addIssue({ code: "custom", path: ["body"], message: SPEAKER_EMAIL_LINK_MISSING });
    for (const [field, text] of [["subject", v.subject], ["body", v.body]] as const) {
      const unknown = unknownPlaceholders(text);
      if (unknown.length) ctx.addIssue({ code: "custom", path: [field], message: `${unknown.join(", ")} is not a placeholder. Use {first name}, {event title}, {event date}, {deadline} or {link}.` });
    }
  });
export type SpeakerEmailBody = z.infer<typeof speakerEmailBody>;
export const speakerRequestBody = z.object({ registrationIds: z.array(uuid).min(1, "Choose at least one person.").max(200) });
export type SpeakerRequestBody = z.infer<typeof speakerRequestBody>;

/** The speaker's own details form (public, personal link). */
export type GuestSpeakerForm = {
  branding: GuestBranding;
  greeting: string;
  event: { title: string; when: string };
  /** "Moderator, Session 2, 11:00" */
  roleLine: string;
  values: {
    name: string;
    title: string;
    organisation: string;
    /** EV-F6 (round 3 feedback): their position (job title), from the record or the guest row. */
    position?: string;
    bio: string;
    photo: FileRef | null;
    travelSupport: boolean | null;
    dietary: string;
    slides: FileRef | null;
    slidesShareConsent: boolean | null;
    /** EV7: from the RSVP before they send the form; null = not answered yet. */
    photoConsent: boolean | null;
  };
  /** They sent their details before (the form shows "You can change your answers"). */
  received: boolean;
  /** EV-F5: "Last confirmed: 1 Nov 2026 via RSVP A2.7" from their record; null when they never confirmed. */
  lastConfirmed?: string | null;
  /** Round 4: the questions asked of this person, in order (the event's form, less what staff left out for them). */
  questions?: SpeakerQuestion[];
  /** Round 4: their answers to the questions staff added. */
  answers?: Record<string, string | boolean>;
  /** Round 4: "Dear Katarina, thank you for moderating." */
  intro?: string;
  closed: boolean;
  closedMessage: string | null;
  maxMb: { photo: number; slides: number };
};
export const speakerFormBody = z.object({
  name: text(120, "Full name"),
  title: textOrEmpty(LIMITS.personTitle),
  organisation: textOrEmpty(LIMITS.organisationName),
  /** EV-F6 (additive): the position (job title). */
  position: textOrEmpty(LIMITS.positionTitle).optional(),
  bio: textOrEmpty(LIMITS.personBio),
  photoFileId: uuid.nullable(),
  travelSupport: z.boolean().nullable(),
  dietary: z.string().trim().max(500, "Dietary requirements must be 500 characters or fewer."),
  slidesFileId: uuid.nullable(),
  slidesShareConsent: z.boolean({ required_error: "Tell us whether we may share your slides." }),
  photoConsent: z.boolean(),
  /** Round 4: answers to the questions staff added (by question id). */
  answers: z.record(z.string().max(40), z.union([z.string().trim().max(2000, "An answer can be at most 2,000 characters."), z.boolean()])).optional(),
  /** Round 4: "… may keep these details on file for this project." (required). */
  consent: z.boolean().optional(),
});
export type SpeakerFormBody = z.infer<typeof speakerFormBody>;
export type InviteOptions = {
  activities: { id: Id; code: string; title: string; locked: boolean; lockMessage: string | null }[];
  contact: { id: Id; name: string } | null;
  lists: { id: Id; name: string; people: number }[];
  /** Round 4: the people selected on the Stakeholders list (?contactIds=), in this organisation. */
  selected?: { id: Id; name: string }[];
};

export type CheckInRow = {
  registrationId: Id;
  name: string;
  organisation: string | null;
  /** online: joined online that day (marked by hand), not checked in at the door. */
  days: { day: string; checkedInAt: string | null; online?: boolean }[];
  /** Additive: how they said they join (online events: always online); null when not known. */
  joining?: JoiningMode | null;
  /** Round 4: their role on the list (the Role column), and whether they came as a walk-in. */
  role?: RegistrationRole;
  roleLabel?: string;
  walkIn?: boolean;
  /** Round 5 (EV11): on the list, but never invited (no invitation sent, no answer). The door list can leave them out. */
  notInvited?: boolean;
};
/** Round 4: a Teams attendance report, uploaded first, for one day of the event. */
export const teamsReportBody = z.object({ fileId: uuid, day: isoDate }).strict();
export type TeamsReportBody = z.infer<typeof teamsReportBody>;
/** `ambiguous` (fix round 1): names without an email that more than one person on the list has; nobody was marked for them. */
export type TeamsReportResult = { marked: number; already: number; notOnList: string[]; ambiguous: string[] };
export type CheckInSheet = {
  /** Additive: the event format; online and hybrid events show online presence. */
  format?: EventFormat;
  opensOnTheDay: boolean;
  notice: string | null;
  /** open: check-in is possible for that day (it has come; later days show "—"). */
  days: { day: string; label: string; open: boolean }[];
  rows: CheckInRow[];
  checkedIn: number;
  /** Everyone expected at the door: the people not invited only once they checked in. */
  expected: number;
  /** Round 5 (EV11): the figures of each day, so a day's view shows that day. */
  byDay?: { day: string; checkedIn: number; expected: number }[];
  /** Additive: "This event is closed. Reopen it to change attendance." while the event is closed; nothing can be changed. */
  closedMessage?: string | null;
};
export const checkInBody = z.object({ registrationId: uuid, day: isoDate, online: z.boolean().optional() });
export const genderV3 = z.enum(GENDER_ANSWERS);
export const walkInBody = z.object({
  name: text(LIMITS.userName, "Name"),
  organisation: textOrEmpty(LIMITS.organisationName),
  country: country.nullable(),
  email: email.nullable(),
  gender: genderV3.nullable(),
  /** Round 5 (AN2): the type of their organisation; optional. */
  stakeholderType: z.enum(STAKEHOLDER_TYPES).nullable().optional(),
  /** Consent to keep this record for the project period (required). */
  consent: z.boolean(),
  /** Fix round 2: "Keep me informed about the project" (optional): the only consent to future contact. */
  keepInformed: z.boolean().optional(),
  day: isoDate,
});
export type WalkInBody = z.infer<typeof walkInBody>;

export type FollowUp = {
  thankYouMessage: string;
  feedbackSummary: { questions: number; editPath: string };
  sentTo: string;
  when: string;
  attachments: FileRef[];
  evidence: {
    key: "attendance" | "feedback" | "speakers" | "photos" | "minutes" | "presentations" | "visibility" | "other" | "agenda" | "report";
    label: string;
    source: string;
    status: StatusChip;
    action: Link;
    /** W3: the activity must produce this proof (copied from its line of work). */
    required?: boolean;
    /** Round 5 (EV5): upload a file for this row inside the event (POST .../evidence { fileId, role }); hint says what fits. */
    upload?: { role: string; hint: string };
    /** AC-F9: documents already on the activity whose name says they are this proof (POST .../evidence/existing { fileId, role }). */
    suggest?: { fileId: Id; name: string }[];
  }[];
  sessionSummaries: string;
  outcomes: string;
  /** Marius's event space, section 9: photos are shared only after someone checked that these people are not in them. */
  photoCheck?: {
    /** The names: staff only (empty for partners, see namesHidden). */
    noPhotos: { name: string; organisation: string }[];
    /** How many people did not agree to photos. */
    count?: number;
    /** True for partners: they see how many, not who. */
    namesHidden?: boolean;
    checkedAt: string | null;
  };
  /** The speakers' slides and whether they are shared with the participants (section 7). */
  presentations?: FollowUpPresentation[];
  /** Additive: the last step, "Close the event" (Marius's event space, section 4 Follow-up). */
  closing?: EventClosing;
  /** Round 4 (D7): step 1, sending the feedback form by hand. */
  sending?: FeedbackSending;
  /** Round 4: step 2, "Thank the speakers". */
  speakerThanks?: SpeakerThanks;
  /** Round 4: "2 of 7 ready" over the evidence rows (Other is optional and not counted). */
  evidenceReady?: { ready: number; total: number };
  /** Round 4: the close-out checklist above "Close the event". */
  closeOut?: CloseOutItem[];
};

/** Round 4 (D7): who gets the feedback form. */
export const FEEDBACK_AUDIENCES = ["checked_in", "confirmed", "speakers"] as const;
export type FeedbackAudience = (typeof FEEDBACK_AUDIENCES)[number];
export const FEEDBACK_AUDIENCE_LABELS: Record<FeedbackAudience, string> = {
  checked_in: "Everyone who checked in",
  confirmed: "Everyone confirmed",
  speakers: "Speakers and moderators only",
};

export type FeedbackSending = {
  audience: FeedbackAudience;
  /**
   * Each audience with how many people with an email it reaches now (count)
   * and how many are in it (total: the number the Overview and Feedback
   * results show; more than count when some have no email address or asked
   * not to be contacted).
   */
  audiences: {
    value: FeedbackAudience;
    label: string;
    count: number;
    total?: number;
    /** Additive (EV2), speakers only: everyone on the programme, experts and those not confirmed yet included (the Overview's count). */
    onProgramme?: number;
  }[];
  /** "Send automatically": off for new events (D7). */
  autoSend: boolean;
  /** "The day after the event, at 10:00" or the chosen send time. */
  autoSendLine: string;
  reminderDays: number | null;
  /** Null until it went out. */
  sent: { at: string; count: number; answered: number; line: string; remindedLine: string | null } | null;
  /** Staff editors (not partners, not viewers): may change who gets it and when. */
  canChange: boolean;
  /** Staff editors, not sent yet, not cancelled. */
  canSend: boolean;
  /** Why "Remind now" is not possible (null: it is). */
  remindReason: string | null;
  /** The chip on step 1: "Not sent yet" or "Sent". */
  status: StatusChip;
};
export const feedbackSendingBody = z.object({
  audience: z.enum(FEEDBACK_AUDIENCES),
  autoSend: z.boolean(),
  reminderDays: z.number().int().min(1).max(60).nullable(),
});
export type FeedbackSendingBody = z.infer<typeof feedbackSendingBody>;
export const feedbackSendBody = z.object({ audience: z.enum(FEEDBACK_AUDIENCES) });

export type SpeakerThanks = {
  /** Speakers and moderators on the programme who may be emailed. */
  count: number;
  /** Of those, how many have not sent slides. */
  withoutSlides: number;
  /** "Sent 9 October to 5 speakers and moderators", or null. */
  sentLine: string | null;
  canSend: boolean;
  status: StatusChip;
};
export const thankSpeakersBody = z.object({ includeScores: z.boolean(), askSlides: z.boolean() });
export type ThankSpeakersBody = z.infer<typeof thankSpeakersBody>;

export type CloseOutItem = { key: "attendance" | "feedback" | "evidence" | "budget"; label: string; value: string; done: boolean };
/**
 * Close the event (Marius's event space, sections 4 and 7: Open -> Closed, can
 * reopen). Closing locks the attendance list and marks the activity done.
 */
export type EventClosing = {
  closed: boolean;
  /** "Closed on 12 October 2026 by Ana Petrova", or null while open. */
  closedLine: string | null;
  /** What closing does, in plain words (listed in the confirmation). */
  does: string[];
  /** What is still open; closing is allowed anyway (they show as missing in the report). */
  openPoints: string[];
  /** The report the activity goes into ("Interim report 1 (January 2026 to December 2026)"), when the reporting calendar has a period for it. */
  report: { label: string; path: string | null } | null;
  /** Editors only; the event must not be cancelled and its last day must have come. */
  canClose: boolean;
  canReopen: boolean;
  /** Why it cannot be closed yet (for editors), or null. */
  reason: string | null;
  /** EV-N5: what makes closing (counted as done) look wrong: no approved ToR, nobody on the attendance list. */
  warnings?: string[];
  /** EV-N5: "Cancel the event" instead (not counted as done), for editors while it is open. */
  canCancel?: boolean;
  /** EV-N5: the event was cancelled (not counted as done); editors may undo it. */
  cancelled?: boolean;
  canUncancel?: boolean;
};
// ---------- costs logged in the event (Logistics; review E3) ----------

/**
 * The event budget at its final figures, stored when the event is closed:
 * per budget line the ToR amount (the estimate) and the final cost (what was
 * spent). The project budget then counts the final cost instead of the ToR
 * amount; the rest goes back to the line. Cleared on reopen.
 */
export type EventFinalCosts = {
  closedAt: string;
  torCents: number | null;
  finalCents: number;
  lines: { budgetLineId: Id | null; name: string; torCents: number; finalCents: number }[];
};

export type EventCostLine = {
  /** null: ToR money not linked to a budget line. */
  budgetLineId: Id | null;
  name: string;
  /** The line number, for the funder (shown quiet, in grey). */
  code: string | null;
  /** "per flight", from the budget line; null when the line has no unit. */
  unitLabel: string | null;
  torCents: number;
  committedCents: number;
  spentCents: number;
  leftCents: number;
  /** After closing: the final cost of the line. */
  finalCents: number | null;
  /** "92% of the ToR amount used", "Average €640 a flight, planned €600". */
  warnings: string[];
};

/** Fix round 1: PATCH …/costs/quotes/:linkId, what a quote says. */
export const quotePatch = z.object({ description: z.string().trim().min(1, "Say what the quote is for.").max(1000, "Keep it to 1,000 characters.") }).strict();
export type QuotePatch = z.infer<typeof quotePatch>;
export type EventCostRow = {
  /** commitment id, expense id or file link id (quote). */
  id: Id;
  source: "commitment" | "expense" | "quote";
  type: CostType;
  typeLabel: string;
  counts: CostCounts;
  countsLabel: string;
  date: string;
  description: string;
  supplier: string;
  line: string | null;
  budgetLineId: Id | null;
  /** "3 flights" */
  units: string | null;
  unitsValue: number | null;
  amountCents: number | null;
  file: FileRef | null;
  /** Contracts and orders: Open / Closed, with "€1,600 invoiced"; expenses: their approval status. */
  status: StatusChip | null;
  note: string | null;
  /** "Changed 26 Sep" */
  edited: string | null;
  /** The expense page (invoices and expenses are edited there). */
  path: string | null;
  canEdit: boolean;
  canDelete: boolean;
};

export type EventCosts = {
  /** Contracts, orders, invoices and expenses wait for an approved ToR (quotes do not); the banner text, else null. */
  holdMessage: string | null;
  /** The event is closed: costs are locked until it is reopened. */
  closedMessage: string | null;
  canLog: boolean;
  /** The event has no activity to log costs against (an old event). */
  noActivity: boolean;
  /** "Estimate" while open, "Final" once the event is closed. */
  figuresLabel: "Estimate" | "Final";
  totals: { torCents: number | null; committedCents: number; spentCents: number; leftCents: number | null; finalCents: number | null };
  lines: EventCostLine[];
  costs: EventCostRow[];
  options: {
    lines: { id: Id; name: string; code: string; unit: string | null; unitLabel: string | null; heading: string | null }[];
    /** Open contracts and orders an invoice can pay. */
    commitments: { id: Id; label: string; budgetLineId: Id }[];
    types: { value: CostType; label: string; counts: string; disabledReason: string | null }[];
  };
};

const costUnits = z.number({ invalid_type_error: "Enter how many." }).min(0, "Cannot be negative.").max(1_000_000, "Too many.");
export const costBody = z
  .object({
    type: z.enum(COST_TYPES),
    date: isoDate,
    description: text(200, "What"),
    supplier: textOrEmpty(200),
    budgetLineId: uuid.nullable(),
    /** How many of the line's unit; with unitPriceCents the amount is units × price. */
    units: costUnits.nullable().default(null),
    unitPriceCents: centsV3().nullable().default(null),
    amountCents: centsV3().nullable().default(null),
    fileId: uuid.nullable().default(null),
    /** Invoices: the contract or order they pay. */
    commitmentId: uuid.nullable().default(null),
  })
  .superRefine((b, ctx) => {
    if (b.type !== "quote" && !b.budgetLineId) ctx.addIssue({ code: "custom", path: ["budgetLineId"], message: "Choose the budget line." });
    if (b.type === "quote" && !b.fileId) ctx.addIssue({ code: "custom", path: ["fileId"], message: "Attach the quote." });
    if (b.type !== "quote" && b.amountCents === null && (b.units === null || b.unitPriceCents === null)) ctx.addIssue({ code: "custom", path: ["amountCents"], message: "Enter the amount, or how many and the price." });
    if (b.commitmentId && b.type !== "invoice") ctx.addIssue({ code: "custom", path: ["commitmentId"], message: "Only an invoice can pay a contract or order." });
  });
export type CostBody = z.input<typeof costBody>;

export const commitmentPatch = z
  .object({
    date: isoDate,
    description: text(200, "What"),
    supplier: textOrEmpty(200),
    budgetLineId: uuid,
    units: costUnits.nullable(),
    unitPriceCents: centsV3().nullable(),
    amountCents: centsV3().nullable(),
    fileId: uuid.nullable(),
    /** Close by hand (e.g. the last invoice was lower), or open again. */
    status: z.enum(COMMITMENT_STATUSES),
  })
  .partial();
export type CommitmentPatch = z.infer<typeof commitmentPatch>;

export type FollowUpPresentation = {
  /** The speaker's guest row. */
  registrationId: Id;
  speaker: string;
  session: string | null;
  file: FileRef | null;
  status: PresentationStatus;
  statusLabel: string;
  /** Not uploaded yet, or the speaker asked not to share: cannot be switched on. */
  locked: boolean;
};
/** Round 5 (EV5): a file uploaded inside the event for one row of "Evidence for the report". */
export const EVIDENCE_UPLOAD_ROLES = ["attendance", "photos", "minutes", "presentations", "visibility", "other", "agenda", "report"] as const;
export const evidenceUploadBody = z.object({ fileId: uuid, role: z.enum(EVIDENCE_UPLOAD_ROLES) }).strict();
export type EvidenceUploadBody = z.infer<typeof evidenceUploadBody>;
/** AC-F9: a document already linked to the activity or event, used as this proof (no new upload). */
export const evidenceExistingBody = evidenceUploadBody;
export const followUpBody = z.object({
  thankYouMessage: textOrEmpty(10_000),
  attachmentFileIds: z.array(uuid).max(30),
  /** "I have checked the photos": required while any photo is attached. */
  photosChecked: z.boolean().optional(),
  /** The speakers (guest rows) whose slides are shared; the others are not. Left out = no change. */
  sharedPresentationIds: z.array(uuid).max(200).optional(),
  sessionSummaries: textOrEmpty(50_000),
  outcomes: textOrEmpty(20_000),
});
export type FeedbackForm = {
  thankYouMessage: string;
  questions: QuestionV3[];
  sendAt: string;
  reminderDays: number | null;
  indicator: { id: Id; label: string } | null;
  /** kind, code, shortName, name (R3): the question editor offers shares from answers for "Counts towards indicator…". */
  indicatorOptions: { id: Id; label: string; satisfaction?: boolean; kind?: IndicatorKind; code?: string; shortName?: string | null; name?: string }[];
  /** v3 (additive): the personal feedback links went out. */
  sent?: boolean;
  /** Round 4 (D7): send automatically at sendAt (off for new events). */
  autoSend?: boolean;
  /** E13: "Ask how useful each session was" (off for new events). */
  askSessions?: boolean;
  /** R2-036 (additive): the standard questions not on the form, which staff can put back. */
  removedStandard?: { key: FeedbackStandardKey; label: string }[];
};
export const feedbackFormBody = z.object({
  thankYouMessage: textOrEmpty(10_000),
  sendAt: z.string().datetime({ offset: true }),
  reminderDays: z.number().int().min(1).max(60).nullable(),
  indicatorId: uuid.nullable(),
  /** Round 4 (D7): left out = no change. */
  autoSend: z.boolean().optional(),
  /** E13: "Ask how useful each session was"; left out = no change. */
  askSessions: z.boolean().optional(),
});
export type FeedbackGroup = { label: string; average: number | null; count: number | null; hidden?: boolean };
export type FeedbackResults = {
  example: boolean;
  tiles: { responses: string; usefulPct: string; attendAgainPct: string; indicator: string | null };
  /** pct (round 4): the share of the scale answers, shown on the bar. */
  scale: { value: 1 | 2 | 3 | 4 | 5; label: string; count: number; pct?: number }[];
  /** id (reporting board 3): the answer's own id, for "Save for the report"; none on example results. */
  textAnswers: { id?: Id; answer: string; from: string }[];
  totalTextAnswers: number;
  /** Groups under 5 are merged into "Everyone else" / "Other countries"; with fewer than 5 answers in all, one hidden row (average and count null). */
  byGroup: FeedbackGroup[];
  byCountry: FeedbackGroup[];
  /** "Groups under 5 are not shown separately…" when a group was merged or hidden. */
  smallGroupsNote?: string | null;
  reports: { id: Id; title: string }[];
  /** v3 (additive): the small line under each tile, and the question labels. */
  tileNotes?: { responses: string; useful: string; attendAgain: string; indicator: string | null };
  scaleLabel?: string;
  textLabel?: string | null;
  /** Additive: the gender balance of the people who came; null when the event does not track it. */
  balance?: GenderBalanceView | null;
  /** Round 4: Response rate, Found it useful, Average score, Would come again. */
  cards?: { key: "rate" | "useful" | "average" | "again"; label: string; value: string; note: string }[];
  /** Round 4: "Counts towards indicator 1.2 · …" and "Target 80% · this event 88% · above target". */
  indicatorTarget?: { line: string; comparison: string | null; tone: "success" | "warning" | "neutral" } | null;
  /** Round 4: how many answered the scale question ("41 answers"). */
  scaleAnswers?: number;
  /** Round 4: the score per programme session; a session scored by fewer than 5 people shows no average. */
  bySession?: { label: string; people: string; average: number | null; hidden: boolean }[];
  /** Round 4, under "By gender": how many are non-binary, described themselves or did not share (never a number under 5). */
  genderNote?: string | null;
  /**
   * Round 5 (LF6): every indicator this event's feedback counts towards (each linked
   * question's and the event's own), with this event's figure ("67%" or "4 people"),
   * how many answered, and a warning below the indicator's minimum number of answers.
   * Empty on example results.
   */
  linkedIndicators?: FeedbackIndicatorLink[];
  /** E11: a summary per Stars, 0 to 10, Ranking and Grid question, in form order. */
  questionSummaries?: FeedbackQuestionSummary[];
};
/**
 * E11: Stars and 0 to 10 give the average (of 5 or of 10); Ranking the average
 * rank per option (1 = first, best first); Grid how many chose each column, per row.
 */
export type FeedbackQuestionSummary = {
  id: Id;
  label: string;
  kind: "stars_5" | "scale_10" | "ranking" | "grid";
  answers: number;
  average?: number | null;
  outOf?: number;
  ranking?: { option: string; averageRank: number | null }[];
  grid?: { columns: string[]; rows: { row: string; counts: number[] }[] };
};
export type FeedbackIndicatorLink = {
  id: Id;
  code: string;
  name: string;
  /** The questions that count, by their label; empty when the usefulness answers count. */
  questions: string[];
  value: string | null;
  answers: number;
  target: string | null;
  warning: string | null;
  /** Readings are made from these answers by themselves (one per event). */
  readingNote: string;
};
export const feedbackUseBody = z.object({
  includeInReportId: uuid.nullable(),
  addReadingToIndicatorId: uuid.nullable(),
  /** Round 4: "Include the quotes (without names)" in the report summary. */
  includeQuotes: z.boolean().optional(),
});

// ---------- 8.2 guest and room views ----------

export type GuestBranding = {
  banner: FileRef | null;
  orgLogo: FileRef | null;
  partnerLogos: FileRef[];
  showEuStatement: boolean;
  orgName: string;
  projectName: string;
  privacyUrl: string;
};
export type GuestEvent = { title: string; description: string; when: string; where: string; address: string | null; mapUrl: string | null; organisedBy: string };
export type GuestCalendar = { google: string; outlook: string; icsUrl: string };
/** E1 (retest 30 Oct): a speaker or moderator as guests see them (confirmed people only, and only when the event shows them, E6). */
export type GuestProgrammePerson = { role: "speaker" | "moderator"; name: string; jobTitle: string; organisation: string };
export type GuestProgrammeBlock = { id: Id; time: string; title: string; description: string; parallel: boolean; people: GuestProgrammePerson[] };
/**
 * E1 (retest 30 Oct, wireframe PartGuestProgramme): the programme for guests.
 * The event card (title, subtitle = the RSVP form's invitation text when staff
 * wrote one, When, Where, practical details), "About the event" (empty blocks
 * left out) and the programme day by day.
 */
export type GuestProgramme = {
  card: { title: string; subtitle: string | null; when: string; where: string; address: string | null; mapUrl: string | null; practical: string | null };
  about: { heading: string; body: string }[];
  days: { day: number; label: string; blocks: GuestProgrammeBlock[] }[];
  /** The event shows its confirmed speakers and moderators (E6). */
  showPeople: boolean;
  language: RsvpLanguage;
  /** UI-GL1 (retest round 3): the staff preview only: the guest header (banner, logos, EU emblem) as on the RSVP pages. */
  branding?: GuestBranding;
};
export const answerValue = z.union([
  z.string().max(LIMITS.answerText),
  z.number(),
  z.array(z.string().max(LIMITS.questionOption)).max(20),
  z.boolean(),
  /** E11 (Grid): the column chosen per row. */
  z.record(z.string().max(LIMITS.questionOption), z.string().max(LIMITS.questionOption)),
]);
export const rsvpAnswer = z.object({
  attendance: z.enum(["all_days", "day_1", "day_2", "no", "maybe"]),
  organisation: textOrEmpty(LIMITS.organisationName),
  position: textOrEmpty(LIMITS.positionTitle),
  gender: genderV3.nullable(),
  /** Round 5 (AN2, additive): the type of their organisation; optional. */
  stakeholderType: z.enum(STAKEHOLDER_TYPES).nullable().optional(),
  travelSupport: z.boolean().nullable(),
  dietary: textOrEmpty(500),
  photoConsent: z.boolean(),
  /** EV-F6 (round 3 feedback, additive): title ("Dr", "Ms"), country and phone, all optional. */
  title: textOrEmpty(LIMITS.personTitle).optional(),
  country: country.nullable().optional(),
  phone: textOrEmpty(LIMITS.phone).optional(),
  /** EV-F3/EV-F5 (additive): accessibility needs, kept on the person for the next event. */
  accessNeeds: textOrEmpty(500).optional(),
  /** "Store my details to organise this event" (required to say yes on the guest pages). */
  consentKeep: z.boolean(),
  /** Fix round 1 (C-3): "Keep me informed about the project", optional; the only tick that is consent to future contact. */
  keepInformed: z.boolean().optional(),
  answers: z.record(uuid, answerValue),
  /** "How will you join?": asked for hybrid events only. */
  joining: z.enum(JOINING_MODES).nullable().optional(),
});
export type RsvpAnswer = z.infer<typeof rsvpAnswer>;
export type GuestInvite = {
  preview: boolean;
  branding: GuestBranding;
  greeting: string;
  event: GuestEvent;
  calendar: GuestCalendar;
  /** Round 5 (EM9, additive): the gender on the guest's row fills the gender question in advance. */
  fromRecord: {
    organisation: string | null;
    position: string | null;
    gender?: GenderAnswer | null;
    /** Round 5 (AN2, additive). */
    stakeholderType?: StakeholderType | null;
    /** EV-F5 (round 3 feedback, additive): the rest of their event profile, to pre-fill and ask "Is this still correct?". */
    title?: string | null;
    country?: string | null;
    /** This registration's own answers only (the link can be forwarded): never the record's phone or needs. */
    phone?: string | null;
    dietary?: string | null;
    accessNeeds?: string | null;
    /** The record has dietary or accessibility needs on file (the values are not shown on the guest page). */
    needsOnFile?: boolean;
    /** "1 Nov 2026 via RSVP A2.7"; null when they never confirmed. */
    lastConfirmed?: string | null;
  };
  questions: QuestionV3[];
  standard: { gender: "off" | "optional" | "required"; travelSupport: boolean; dietary: boolean; photoConsent: boolean };
  answer: RsvpAnswer | null;
  /** Round 5 (EM7, additive): the guest's answer as it stands; null before they answered. */
  answerStatus?: "registered" | "waitlisted" | "declined" | "maybe" | null;
  closed: boolean;
  closedMessage: string | null;
  /** v3 (additive): the event days, for the per-day attendance answer. */
  days?: { day: string; label: string }[];
  /** Additive: "ask" shows "How will you join?" (hybrid); online and in_person do not ask. */
  joining?: "ask" | JoiningMode;
  /** Additive: all places are taken (waiting list or full), shown above the form; null otherwise. */
  fullMessage?: string | null;
  /** Round 4: the language of the page (its fixed wording and the questions). */
  language?: RsvpLanguage;
  /** Round 5: the invitation text, filled for this guest ("Dear Ana, ..."); plain text, paragraphs split by blank lines. */
  intro?: string;
  /** Round 5: the order of the movable questions (standard keys and question ids, see rsvpQuestionOrder). R2-036: without the removed ones. */
  order?: string[];
  /** R2-036 (additive): the standard questions staff took off the form: not shown, not required. */
  removed?: RsvpStandardQuestion[];
  /** E1 (retest 30 Oct, additive): the programme for guests. */
  programme?: GuestProgramme;
};
export type RsvpResult = { status: "registered" | "waitlisted" | "declined"; email: string | null };
export type GuestConfirmed = {
  branding: GuestBranding;
  email: string | null;
  event: GuestEvent;
  calendar: GuestCalendar;
  whatNext: string[];
  changePath: string;
  /** v3 (additive): the registration's state ("registered", "waitlisted", "declined"...), preview mode, automatic calendar updates. */
  status?: string;
  preview?: boolean;
  updatesCalendar?: boolean;
  /** Additive: the person's "Your information" page (see or withdraw what they shared); null in a preview or without a stakeholder record. */
  yourInfoPath?: string | null;
  /** E1 (retest 30 Oct, additive): the programme for guests. */
  programme?: GuestProgramme;
};
export type GuestFeedback = {
  preview: boolean;
  branding: GuestBranding;
  greeting: string;
  thankYouMessage: string;
  presentations: FileRef[];
  photos: FileRef[];
  questions: QuestionV3[];
  answered: boolean;
  /** v3 (additive): the earlier answer, shown when the guest comes back to replace it. */
  answers?: Record<Id, string | number | string[] | boolean | Record<string, string>> | null;
  /**
   * Round 4: the programme sessions to score (optional, 1 to 5), and the earlier scores.
   * E13: only when "Ask how useful each session was" is on; titles only, never
   * the speakers' or moderators' names.
   */
  sessions?: { id: Id; title: string }[];
  sessionScores?: Record<Id, number>;
};
export const guestFeedbackBody = z.object({
  answers: z.record(uuid, answerValue),
  sessionScores: z.record(uuid, z.number().int().min(1, "Choose a number from 1 to 5.").max(5, "Choose a number from 1 to 5.")).optional(),
});
export type RoomGuest = { registrationId: Id; name: string; organisation: string | null; checkedIn: boolean };
export type RoomView = {
  event: { title: string; place: string; dayLabel: string; openingText: string };
  logos: FileRef[];
  showEuStatement: boolean;
  orgName: string;
  firstGuests: RoomGuest[];
};
export const roomSearchQuery = z.object({ q: z.string().trim().min(2, "Type at least 2 letters.").max(60) });
export const roomCheckInBody = z.object({ registrationId: uuid });
export const roomWalkInBody = z.object({
  name: text(LIMITS.userName, "Name"),
  organisation: textOrEmpty(LIMITS.organisationName),
  email: email.nullable(),
  consent: z.literal(true),
});
export type RoomAnswer = { firstName: string; message: string };
export type RoomOpened = { roomPath: string };
export const eventDescription = textOrNull(LIMITS.eventDescription);

// ---------- round 4: the invitation email (Invitations › Invitation email) ----------

export const INVITATION_PLACEHOLDERS = ["{first name}", "{salutation}", "{organisation}"] as const;
/** The values for one person. */
export type InvitationValues = { firstName: string; salutation: string; organisation: string };

const INVITATION_PLACEHOLDER = /\{\s*([a-z ]+?)\s*\}/gi;

/** Fills {first name}, {salutation} and {organisation} ({First name} counts too). Unknown ones stay as they are. Runs on the server (the email) and in the browser (the preview). */
export function fillInvitation(text: string, v: InvitationValues): string {
  const map: Record<string, string> = { "first name": v.firstName, salutation: v.salutation, organisation: v.organisation };
  return text.replace(INVITATION_PLACEHOLDER, (whole, name: string) => map[name.toLowerCase().replace(/\s+/g, " ")] ?? whole);
}

/** "Ms Jovanović", "Mr Krasniqi"; the full name when the gender is not known. */
export function salutationFor(name: string, gender: string | null | undefined): string {
  const trimmed = name.trim();
  const last = trimmed.split(/\s+/).slice(-1)[0] ?? trimmed;
  if (gender === "female") return `Ms ${last}`;
  if (gender === "male") return `Mr ${last}`;
  return trimmed;
}

export const INVITATION_FROM = ["organisation", "me"] as const;
export type InvitationFrom = (typeof INVITATION_FROM)[number];

export const invitationEmailBody = z
  .object({
    subject: z.string().trim().min(1, "Enter a subject.").max(200, "The subject must be 200 characters or fewer."),
    message: z.string().trim().min(1, "Write the message.").max(LIMITS.eventDescription, "The message is too long."),
    from: z.enum(INVITATION_FROM),
    attachmentFileIds: z.array(uuid).max(10, "Attach at most 10 files."),
    saveTheDate: z.boolean(),
  })
  .strict();
export type InvitationEmailBody = z.infer<typeof invitationEmailBody>;

/** Send to the ticked people, or (no ids) to everyone on the list not invited yet. */
export const sendInvitationsBody = z
  .object({
    registrationIds: z.array(uuid).min(1).max(1000).optional(),
    /** Round 5 (AC9): an optional note, asked in the confirmation dialog and kept in the history. */
    note: z.string().trim().max(300, "Keep the note under 300 characters.").optional(),
  })
  .strict();
export type SendInvitationsBody = z.infer<typeof sendInvitationsBody>;

export type InvitationEmailView = {
  /** Who gets it: the ticked people from People, else everyone on the list not invited yet. */
  to: { mode: "selected" | "not_invited"; people: { id: Id; name: string }[]; count: number };
  fromOptions: { value: InvitationFrom; label: string }[];
  subject: string;
  message: string;
  from: InvitationFrom;
  attachments: FileRef[];
  saveTheDate: boolean;
  defaults: { subject: string; message: string };
  /** The first person's values, for the preview. */
  sample: InvitationValues & { name: string };
  preview: { fromLabel: string; when: string; where: string; onlineLabel: string | null; deadline: string | null; rsvpHint: string };
  branding: { banner: FileRef | null; orgLogo: FileRef | null; partnerLogos: FileRef[]; showEuStatement: boolean };
  canSend: boolean;
  /** Why sending is not possible (ToR, closed, RSVP form still a draft, nobody to send to, read-only). */
  sendReason: string | null;
  /** EV-N2: the RSVP form is still a draft, so nothing can be sent until it is finalised. */
  rsvpDraft: boolean;
  /** "Finalise and create link" from the send screen (staff editors, while it is a draft). */
  canFinaliseRsvp: boolean;
};

// ---------- round 4 (D8): the shared RSVP link /r/:slug ----------

/** The RSVP form is a draft until staff finalise it; then it has one link to share with everyone ("Your RSVP link · Live"). */
export type RsvpLinkView = { state: "draft" | "live"; url: string | null; finalisedAt: string | null };

export const RSVP_LINK_CONSENT_MISSING = "Tick the box to agree that we store your details. We need it to register you.";

/**
 * An answer through the shared link: the person's name and email as well,
 * the required consent to store their details, the bot check token and an
 * empty trap field ("website") that people never see.
 */
export const rsvpLinkAnswer = rsvpAnswer.extend({
  name: text(120, "Your name"),
  email,
  consentKeep: z.literal(true, { errorMap: () => ({ message: RSVP_LINK_CONSENT_MISSING }) }),
  botToken: z.string().max(4096).optional(),
  website: z.string().max(200).optional(),
});
export type RsvpLinkAnswer = z.infer<typeof rsvpLinkAnswer>;

/** The public page of the shared link. */
export type GuestRsvpLink = Omit<GuestInvite, "answer" | "greeting" | "fromRecord" | "preview"> & {
  /** The Cloudflare Turnstile site key when the bot check is on; null = no check configured. */
  botCheckSiteKey: string | null;
};
/**
 * The same answer whatever happened, so the link never tells who is on the
 * guest list. EV-F2: every reply through the shared link is confirmed from
 * the person's inbox, so the page always says "Check your email".
 */
export type RsvpLinkResult = { ok: true; email: string };

// ---------- round 4: Invitations › Automatic emails ----------

export const AUTOMATIC_EMAILS = ["rsvpReminder", "confirmation", "practicalInfo", "programme", "changes"] as const;
export type AutomaticEmailKey = (typeof AUTOMATIC_EMAILS)[number];
export const AUTOMATIC_EMAIL_PLACEHOLDERS = ["{first name}", "{salutation}", "{event title}", "{event date}", "{deadline}"] as const;
export type AutomaticEmailValues = { firstName: string; salutation: string; eventTitle: string; eventDate: string; deadline: string };

/** Fills the placeholders of an automatic email ({First name} counts too). Unknown ones stay. Server (the email) and browser (the preview). */
export function fillAutomaticEmail(text: string, v: AutomaticEmailValues): string {
  const map: Record<string, string> = { "first name": v.firstName, salutation: v.salutation, "event title": v.eventTitle, "event date": v.eventDate, deadline: v.deadline };
  return text.replace(/\{\s*([a-z ]+?)\s*\}/gi, (whole, name: string) => map[name.toLowerCase().replace(/\s+/g, " ")] ?? whole);
}

/** What staff changed for one email; null = the default. Stored per event (event.email_settings). */
export type AutomaticEmailSetting = {
  subject?: string | null;
  message?: string | null;
  days?: number | null;
  time?: string | null;
  /** Fix round 1: what a timed email last went out for (the RSVP deadline or the event's start), so a new time never sends it again. */
  sentFor?: string | null;
};
export type AutomaticEmailSettings = Partial<Record<AutomaticEmailKey, AutomaticEmailSetting>>;

export const automaticEmailBody = z
  .object({
    on: z.boolean(),
    /** Days before the RSVP deadline (reminder to reply) or the event (programme and last details). */
    days: z.number({ invalid_type_error: "Enter a number of days." }).int().min(1, "Enter a number of days from 1 to 60.").max(60, "Enter a number of days from 1 to 60.").nullable().optional(),
    /** The time of day it goes out (Brussels time). */
    time: timeOfDay.nullable().optional(),
    /** null = the default text ("Reset to the default text"). */
    subject: z.string().trim().max(200, "The subject must be 200 characters or fewer.").nullable(),
    message: z.string().trim().max(LIMITS.eventDescription, "The message is too long.").nullable(),
    /** M10, Practical information only: when registration opens on the first day ("08:30"); null = not said. Left out = no change. */
    registrationTime: timeOfDay.nullable().optional(),
    /** M10, Practical information only: who to call on site ("Ana Petrova · +389 2 000 0000"); "" = nobody. Left out = no change. */
    onSiteContact: z.string().trim().max(200, "The contact on site must be 200 characters or fewer.").optional(),
  })
  .strict();
export type AutomaticEmailBody = z.infer<typeof automaticEmailBody>;

export type AutomaticEmailView = {
  key: AutomaticEmailKey;
  label: string;
  on: boolean;
  /** "5 days before the RSVP deadline". */
  whenLabel: string;
  /** "Goes out on Friday 25 September at 10:00", or why not yet. */
  nextLine: string | null;
  /** Which timing fields apply: days before the deadline or the event, and a time; none = right after something happens. */
  timing: { anchor: "deadline" | "event" | null; days: number | null; time: string | null };
  recipients: string;
  subject: string;
  message: string;
  defaults: { subject: string; message: string };
  /** What is added under the message (calendar invite, programme, the online link). */
  adds: string[];
  /** A text of its own for this email kind: Practical information's message is the practical information. */
  canEditText: boolean;
  /** M10, Practical information only: when registration opens and who to call on site (in the programme email). */
  onSite?: { registrationTime: string | null; contact: string };
};
export type AutomaticEmailsView = {
  emails: AutomaticEmailView[];
  sample: AutomaticEmailValues & { name: string };
  canEdit: boolean;
};

// ---------- round 4: the speaker details form (Invitations › Speaker details) ----------

/** The questions every speaker form has; staff choose whether each is asked and required, and may rename it. */
export const SPEAKER_BUILT_IN = ["name", "title", "organisation", "position", "bio", "photo", "travelSupport", "dietary", "slides", "slidesShareConsent"] as const;
export type SpeakerBuiltIn = (typeof SPEAKER_BUILT_IN)[number];
export const SPEAKER_ANSWER_KINDS = ["text", "long_text", "image", "yes_no", "file"] as const;
export type SpeakerAnswerKind = (typeof SPEAKER_ANSWER_KINDS)[number];
export const SPEAKER_ANSWER_KIND_LABELS: Record<SpeakerAnswerKind, string> = { text: "Text", long_text: "Long text", image: "Image", yes_no: "Yes / No", file: "File" };
/** Answer types of the questions staff add themselves. */
export const SPEAKER_CUSTOM_KINDS = ["text", "long_text", "yes_no"] as const;

/** One question of the form. `key`: a built-in key, or the id of a question staff added. */
export type SpeakerQuestion = { key: string; label: string; kind: SpeakerAnswerKind; ask: boolean; required: boolean; builtIn: boolean };

/** The wireframe's form: full name, title, organisation and biography required; the rest optional, except the slides consent. */
export const DEFAULT_SPEAKER_FORM: SpeakerQuestion[] = [
  { key: "name", label: "Full name", kind: "text", ask: true, required: true, builtIn: true },
  { key: "title", label: "Title", kind: "text", ask: true, required: true, builtIn: true },
  { key: "organisation", label: "Organisation", kind: "text", ask: true, required: true, builtIn: true },
  /** EV-F6 (round 3 feedback): the position, asked as well (optional). */
  { key: "position", label: "Position", kind: "text", ask: true, required: false, builtIn: true },
  { key: "bio", label: "Short biography (up to 100 words)", kind: "long_text", ask: true, required: true, builtIn: true },
  { key: "photo", label: "Photo", kind: "image", ask: true, required: false, builtIn: true },
  { key: "travelSupport", label: "Do you need travel support?", kind: "yes_no", ask: true, required: false, builtIn: true },
  { key: "dietary", label: "Dietary requirements", kind: "text", ask: true, required: false, builtIn: true },
  { key: "slides", label: "Your slides (you can also send them after the event)", kind: "file", ask: true, required: false, builtIn: true },
  { key: "slidesShareConsent", label: "May we share your slides with participants?", kind: "yes_no", ask: true, required: true, builtIn: true },
];

export const speakerFormConfigBody = z
  .object({
    questions: z
      .array(
        z.object({
          /** A built-in key, the id of a question added before, or "new" for one added now. */
          key: z.string().trim().min(1).max(40),
          label: text(200, "Question"),
          kind: z.enum(SPEAKER_ANSWER_KINDS),
          ask: z.boolean(),
          required: z.boolean(),
        }),
      )
      .max(30, "A form can have at most 30 questions."),
  })
  .strict();
export type SpeakerFormConfigBody = z.infer<typeof speakerFormConfigBody>;

/** "Customise" next to a person: the questions left out for them. */
export const speakerSkipBody = z.object({ skip: z.array(z.string().trim().min(1).max(40)).max(30) }).strict();
export type SpeakerSkipBody = z.infer<typeof speakerSkipBody>;

export const SPEAKER_CONSENT_MISSING = "Tick the box to agree that we keep these details on file.";

// ---------- round 4: People › Export ----------

const flag = z.enum(["0", "1"]).transform((v) => v === "1");
/** The list of participants export (wireframe "Suite Participants Export"): who, columns, sort. */
export const PARTICIPANTS_NO_COLUMN = "Choose at least one column.";
export const participantsExportQuery = z
  .object({
    who: z.enum(["coming", "invited", "checked_in"]).default("coming"),
    /** E15 (retest 30 Oct): name, job title and organisation are each on or off. */
    name: flag.default("1"),
    title: flag.default("1"),
    organisation: flag.default("1"),
    country: flag.default("0"),
    signature: flag.default("0"),
    /** E15: the event's banner and logos (as on the invitation) at the top of the file. */
    banner: flag.default("1"),
    sort: z.enum(["organisation", "surname"]).default("organisation"),
  })
  .refine((q) => q.name || q.title || q.organisation || q.country, { message: PARTICIPANTS_NO_COLUMN, path: ["columns"] });
export type ParticipantsExportQuery = z.output<typeof participantsExportQuery>;
export type ParticipantsPreview = {
  title: string;
  /** "Regional workshop · 7–8 October 2026 · Skopje" */
  meta: string;
  columns: string[];
  rows: string[][];
  /** For the choices: "Everyone coming (7)", "Everyone invited (8)". */
  counts: { coming: number; invited: number };
  /** E15: the banner and logos the file starts with (PNG and JPEG only); null when switched off or there are none. */
  branding: { banner: FileRef | null; logos: FileRef[] } | null;
};
